import React, { useState } from 'react';
import Navbar from './Components/Navbar';
import { apiUrl } from '../api';
import Footer from '../Components/Footer';
import {
  Loader,
  CheckCircle,
  Clock,
  ShoppingBag,
  Truck,
  PackageCheck,
  XCircle,
  Store,
  Box,
  ArrowRight,
  ShieldCheck,
} from 'lucide-react';
import './MyOrders.css';

const TRACK_COLOR = '#523B34';

const HD_STATUSES = [
  {
    key: 'confirmed',
    label: 'Confirmed',
    desc: 'Order has been confirmed.',
    Icon: ShieldCheck,
  },
  {
    key: 'processing',
    label: 'Processing',
    desc: 'We are preparing your order.',
    Icon: Loader,
  },
  {
    key: 'out_for_delivery',
    label: 'Out for Delivery',
    desc: 'It is on the way.',
    Icon: Truck,
  },
  {
    key: 'delivered',
    label: 'Delivered',
    desc: 'Your order was delivered.',
    Icon: PackageCheck,
  },
  {
    key: 'cancelled',
    label: 'Cancelled',
    desc: 'Order has been cancelled.',
    Icon: XCircle,
  },
];

const PU_STATUSES = [
  {
    key: 'confirmed',
    label: 'Confirmed',
    desc: 'Order has been confirmed.',
    Icon: ShieldCheck,
  },
  {
    key: 'ready_for_pickup',
    label: 'Ready for Pickup',
    desc: 'Order is ready for pickup.',
    Icon: Store,
  },
  {
    key: 'picked_up',
    label: 'Picked Up',
    desc: 'Order has been picked up by you.',
    Icon: ShoppingBag,
  },
  {
    key: 'cancelled',
    label: 'Cancelled',
    desc: 'Order has been cancelled.',
    Icon: XCircle,
  },
];

function getActiveStep(statusList, orderStatus) {
  const idx = statusList.findIndex(({ key }) => key === orderStatus);
  // if "cancelled", always highlight only Cancelled
  if (orderStatus === 'cancelled') {
    return statusList.length - 1;
  }
  return idx >= 0 ? idx : 0;
}

function getFormattedSlot(orderType, order) {
  const d = order && order.delivery;
  if (!d || !d.date) return '';
  const date = new Date(d.date).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
  const slots = { '12-2': '12:00 - 14:00', '2-4': '14:00 - 16:00', '4-6': '16:00 - 18:00', '6-8': '18:00 - 20:00' };
  const slot = slots[d.timeSlot] || d.timeSlot || '';
  const label = orderType === 'pickup' ? 'Collection' : 'Delivery';
  return `${label}: ${date}${slot ? ', ' + slot : ''}`;
}

export default function MyOrders() {
  const [orderIdInput, setOrderIdInput] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [orderData, setOrderData] = useState(null);
  const [orderType, setOrderType] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Order id + email: the email keeps someone who guesses an order number from
  // reading another customer's details.
  const handleTrackOrder = async (e) => {
    e.preventDefault();
    setError('');
    setOrderData(null);
    setLoading(true);

    try {
      const params = new URLSearchParams({ orderId: orderIdInput.trim(), email: emailInput.trim() });
      const res = await fetch(apiUrl(`/api/myorders/track?${params}`));
      const data = await res.json();

      if (data.success) {
        setOrderData(data.data);
        setOrderType(data.data.orderType);
      } else {
        setError(data.message || 'Order not found. Please check your Order ID and email.');
      }
    } catch {
      setError("We couldn't reach the server. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  const statusList = orderType === 'homedelivery' ? HD_STATUSES : orderType === 'pickup' ? PU_STATUSES : [];
  const activeStep = orderData ? getActiveStep(statusList, orderData.orderStatus) : 0;

  return (
    <div className="orders-main-wrapper">
      <Navbar />
      <div className="track-order-container">
        <div style={{width : '90%', marginBottom : '1rem'}}>
        <p style={{textAlign : 'left', fontSize : '4rem', fontWeight : '500', color : '#391C14'}}>Track Your Order</p>
        </div>
        <form className="track-form" onSubmit={handleTrackOrder}>
          <label htmlFor="orderIdInput" className="track-label">
            Please write your Order ID for Tracking
          </label>
          <input
            id="orderIdInput"
            type="text"
            className="track-input"
            value={orderIdInput}
            onChange={(e) => setOrderIdInput(e.target.value)}
            autoComplete="off"
            placeholder="e.g. ORD_1791438725655_594D2432"
            disabled={loading}
          />
          <label htmlFor="trackEmailInput" className="track-label">
            And the email address you ordered with
          </label>
          <input
            id="trackEmailInput"
            type="email"
            className="track-input"
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            autoComplete="email"
            placeholder="you@example.com"
            disabled={loading}
          />
          <button
            type="submit"
            className="track-submit-btn"
            disabled={loading || !orderIdInput.trim() || !emailInput.trim()}
          >
            {loading ? 'Searching...' : 'Track'}
          </button>
        </form>

        {error && <div className="track-error">{error}</div>}

        {/* Order Details & Timeline */}
        {orderData && (
          <div className="track-details">
            <div className="track-header">
              <div className="track-title">Track Order</div>
              <div className="track-meta">
                <span className="track-date">
                  {new Date(orderData.placedAt).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' })}
                </span>
                <span className="track-id">Order ID: {orderData.orderId}</span>
                <span className="track-amount">Amt: <b>£{Number(orderData.pricing.total).toFixed(2)}</b></span>
              </div>
              <div className="track-eta">Status: <b style={{ color: TRACK_COLOR }}>{statusList[activeStep].label}</b></div>
              <div className="track-slot">{getFormattedSlot(orderType, orderData)}</div>
            </div>

            {/* Timeline */}
            <div className="track-timeline">
              <ul className="track-timeline-list">
                {statusList.map((step, idx) => {
                  let isDone = idx < activeStep;
                  let isActive = idx === activeStep;
                  let isFuture = idx > activeStep && orderData.orderStatus !== 'cancelled';
                  // skip cancelled until it's job is current status
                  if (step.key === 'cancelled' && orderData.orderStatus !== 'cancelled') return null;
                  return (
                    <li
                      key={step.key}
                      className={`track-timeline-step 
                        ${isDone ? 'done' : ''} 
                        ${isActive ? 'active' : ''} 
                        ${isFuture ? 'future':''}
                      `}
                    >
                      <div className={`track-dot`}>
                        {isActive && <step.Icon size={19} color="white" />}
                        {isDone && <CheckCircle size={19} color="white" />}
                        {(!isActive && !isDone) && <step.Icon size={19} color="#b9ab99" />}
                      </div>
                      <div className={`track-timeline-content`}>
                        <div className="track-status-label">{step.label}</div>
                        <div className="track-status-desc">{step.desc}</div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="track-address-box">
              <div className="track-address-title">Delivery Address</div>
              <div className="track-address-info">
                {orderData.customerAddress}
                {orderData.delivery?.postcode ? ` · ${orderData.delivery.postcode}` : ''}
                {orderData.delivery?.area ? ` (${orderData.delivery.area})` : ''}
              </div>
            </div>

            
          </div>
        )}
      </div>
      
    </div>
  );
}
